import React, { useRef, useEffect, useState } from 'react';
import { Camera, X, FlipHorizontal, Zap, ZapOff } from 'lucide-react';

interface CameraCaptureProps {
  onCapture: (canvas: HTMLCanvasElement) => void;
  onClose: () => void;
  isProcessing?: boolean;
}

export default function CameraCapture({
  onCapture,
  onClose,
  isProcessing = false
}: CameraCaptureProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [torch, setTorch] = useState(false);
  const [hasTorch, setHasTorch] = useState(false);

  useEffect(() => {
    const startCamera = async () => {
      try {
        // Stop existing stream
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
        }

        const constraints: MediaStreamConstraints = {
          video: {
            facingMode,
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        };

        const newStream = await navigator.mediaDevices.getUserMedia(constraints);
        streamRef.current = newStream;
        setStream(newStream);

        if (videoRef.current) {
          videoRef.current.srcObject = newStream;
        }

        // Check for torch capability
        const track = newStream.getVideoTracks()[0];
        const capabilities = track.getCapabilities?.() as any;
        setHasTorch(capabilities?.torch === true);

        setError(null);
      } catch (err) {
        console.error('Camera error:', err);
        setError(
          err instanceof DOMException && err.name === 'NotAllowedError'
            ? 'Camera access denied. Please allow camera access in your browser settings.'
            : 'Failed to access camera. Please make sure your device has a camera.'
        );
      }
    };

    startCamera();

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [facingMode]);

  const toggleCamera = () => {
    setFacingMode(mode => mode === 'user' ? 'environment' : 'user');
  };

  const toggleTorch = async () => {
    if (!stream || !hasTorch) return;

    try {
      const track = stream.getVideoTracks()[0];
      await track.applyConstraints({
        advanced: [{ torch: !torch } as any]
      });
      setTorch(!torch);
    } catch (error) {
      console.error('Failed to toggle torch:', error);
    }
  };

  const handleCapture = () => {
    if (!videoRef.current || isProcessing) return;

    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(video, 0, 0);
    onCapture(canvas);
  };

  if (error) {
    return (
      <div className="fixed inset-0 bg-black z-50 flex items-center justify-center p-4">
        <div className="bg-gray-900 rounded-2xl p-6 max-w-md text-center">
          <div className="text-red-400 mb-4">
            <Camera className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>{error}</p>
          </div>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black z-50">
      {/* Video Feed */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className="absolute inset-0 w-full h-full object-cover"
      />

      {/* Overlay UI */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Card Placement Guide */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div
            className="border-2 border-white/50 rounded-lg"
            style={{
              width: '70%',
              maxWidth: '300px',
              aspectRatio: '2.5 / 3.5', // Standard card ratio
            }}
          >
            {/* Corner markers */}
            <div className="absolute -top-1 -left-1 w-6 h-6 border-t-4 border-l-4 border-white rounded-tl-lg" />
            <div className="absolute -top-1 -right-1 w-6 h-6 border-t-4 border-r-4 border-white rounded-tr-lg" />
            <div className="absolute -bottom-1 -left-1 w-6 h-6 border-b-4 border-l-4 border-white rounded-bl-lg" />
            <div className="absolute -bottom-1 -right-1 w-6 h-6 border-b-4 border-r-4 border-white rounded-br-lg" />
          </div>
        </div>

        {/* Instructions */}
        <div className="absolute top-20 left-0 right-0 text-center">
          <p className="text-white text-lg font-medium drop-shadow-lg">
            Position card within the frame
          </p>
          <p className="text-white/70 text-sm mt-1 drop-shadow">
            Make sure the card name is visible
          </p>
        </div>

        {/* Processing indicator */}
        {isProcessing && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <div className="text-center text-white">
              <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-4" />
              <p className="text-lg font-medium">Analyzing card...</p>
            </div>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="absolute bottom-0 left-0 right-0 p-6 pointer-events-auto">
        <div className="flex items-center justify-center gap-6">
          {/* Close button */}
          <button
            onClick={onClose}
            className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 flex items-center justify-center transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>

          {/* Capture button */}
          <button
            onClick={handleCapture}
            disabled={isProcessing}
            className={`w-20 h-20 rounded-full flex items-center justify-center transition-all ${
              isProcessing
                ? 'bg-gray-600'
                : 'bg-white hover:scale-105 active:scale-95'
            }`}
          >
            <div className={`w-16 h-16 rounded-full ${
              isProcessing ? 'bg-gray-500' : 'bg-white border-4 border-gray-900'
            }`} />
          </button>

          {/* Toggle camera */}
          <button
            onClick={toggleCamera}
            className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 flex items-center justify-center transition-colors"
          >
            <FlipHorizontal className="w-6 h-6 text-white" />
          </button>
        </div>

        {/* Torch toggle */}
        {hasTorch && (
          <button
            onClick={toggleTorch}
            className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 flex items-center justify-center transition-colors"
          >
            {torch ? (
              <Zap className="w-5 h-5 text-yellow-400" />
            ) : (
              <ZapOff className="w-5 h-5 text-white" />
            )}
          </button>
        )}
      </div>
    </div>
  );
}
